import React, { createContext, useContext, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, CreditCard, Info, MessageSquare } from "lucide-react";

const NotificationsContext = createContext(null);

const typeMeta = {
  alert: { color: "#ef4444", Icon: AlertTriangle },
  task: { color: "#22c55e", Icon: CheckCircle2 },
  payment: { color: "#0ea5e9", Icon: CreditCard },
  message: { color: "#6366f1", Icon: MessageSquare },
  system: { color: "#64748b", Icon: Info },
};

const withMeta = (type) => typeMeta[type] || typeMeta.system;

const initialNotifications = [
  {
    id: "n1",
    title: "Yeni görev atandı",
    body: "Onboarding checklist'ini bugün tamamla lütfen.",
    actor: { name: "Görev Botu", avatar: "https://i.pravatar.cc/60?img=55" },
    type: "task",
    createdAt: new Date("2026-03-05T09:10:00"),
    unread: true,
    action: { type: "task-intent", payload: { taskId: "TASK-1042" } },
  },
  {
    id: "n2",
    title: "Yeni proje oluşturuldu",
    body: "Digital Exchange projesi açıldı.",
    actor: { name: "PM Bot", avatar: "https://i.pravatar.cc/60?img=41" },
    type: "task",
    createdAt: new Date("2026-03-04T18:45:00"),
    unread: true,
    action: {
      type: "project-intent",
      payload: {
        projectId: "DX-204",
        path: `${"" || ""}/projects/project-detail/DX-204/Dubai`,
      },
    },
  },
  {
    id: "n3",
    title: "Sistem uyarısı",
    body: "Staging API error rate %5'e çıktı.",
    actor: { name: "Monitor", avatar: "" },
    type: "alert",
    createdAt: new Date("2026-03-04T16:20:00"),
    unread: false,
    action: { type: "route", payload: { path: `${"" || ""}/reports/Dubai` } },
  },
  {
    id: "n4",
    title: "Mesajın var",
    body: "Sena Yılmaz: 'QA raporuna baktın mı?'",
    actor: { name: "Sena Yılmaz", avatar: "https://i.pravatar.cc/60?img=33" },
    type: "message",
    createdAt: new Date("2026-03-04T14:05:00"),
    unread: true,
    action: { type: "message-intent", payload: { scope: "direct", id: "i2" } },
  },
  {
    id: "n5",
    title: "Ödeme başarısız",
    body: "BlueWave Cloud için kart reddedildi.",
    actor: { name: "Billing", avatar: "https://i.pravatar.cc/60?img=42" },
    type: "alert",
    createdAt: new Date("2026-03-04T10:12:00"),
    unread: false,
    action: { type: "route", payload: { path: `${"" || ""}/expenses/Dubai` } },
  },
  {
    id: "n6",
    title: "Görev tamamlandı",
    body: "Orientify AI tasarım revizyonu onaylandı.",
    actor: { name: "Design Crew", avatar: "https://i.pravatar.cc/60?img=69" },
    type: "task",
    createdAt: new Date("2026-03-03T19:30:00"),
    unread: false,
    action: { type: "task-intent", payload: { taskId: "TASK-991" } },
  },
  {
    id: "n7",
    title: "Sistem bildirimi",
    body: "Haftalık yedekleme başarıyla tamamlandı.",
    actor: { name: "System", avatar: "" },
    type: "system",
    createdAt: new Date("2026-03-03T07:10:00"),
    unread: false,
    action: { type: "route", payload: { path: `${"" || ""}/reports/Dubai` } },
  },
  {
    id: "n8",
    title: "Yeni yorum",
    body: "Kerem Soy roadmap kartına yorum ekledi.",
    actor: { name: "Kerem Soy", avatar: "https://i.pravatar.cc/60?img=64" },
    type: "message",
    createdAt: new Date("2026-03-02T21:40:00"),
    unread: false,
    action: { type: "message-intent", payload: { scope: "group", id: "g1" } },
  },
  {
    id: "n9",
    title: "Uyarı kaldırıldı",
    body: "API error rate normale döndü.",
    actor: { name: "Monitor", avatar: "" },
    type: "system",
    createdAt: new Date("2026-03-02T12:05:00"),
    unread: false,
    action: { type: "route", payload: { path: `${"" || ""}/reports/Dubai` } },
  },
  {
    id: "n10",
    title: "Ödeme hatırlatma",
    body: "Digital Exchange faturasının son günü bugün.",
    actor: { name: "Finance Bot", avatar: "https://i.pravatar.cc/60?img=48" },
    type: "payment",
    createdAt: new Date("2026-03-01T09:00:00"),
    unread: true,
    action: { type: "route", payload: { path: `${"" || ""}/invoices/Dubai` } },
  },
];

const pad = (n) => String(n).padStart(2, "0");

const formatRelative = (dateLike) => {
  const date = typeof dateLike === "string" ? new Date(dateLike) : dateLike;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.round(diffMs / 60000);
  const diffHours = Math.round(diffMs / 3600000);

  if (diffMins < 1) return "şimdi";
  if (diffMins < 60) return `${diffMins} dk önce`;
  if (diffHours < 24) return `${diffHours} sa önce`;

  const isSameDay = date.toDateString() === now.toDateString();
  if (isSameDay) return `Bugün ${pad(date.getHours())}:${pad(date.getMinutes())}`;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();
  if (isYesterday) return `Dün ${pad(date.getHours())}:${pad(date.getMinutes())}`;

  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

export const NotificationsProvider = ({ children }) => {
  const [items, setItems] = useState(initialNotifications);
  const [handlers, setHandlers] = useState({});

  const sorted = useMemo(
    () => [...items].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [items]
  );

  const unreadCount = useMemo(() => items.filter((n) => n.unread).length, [items]);

  const markRead = (id) =>
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, unread: false } : n)));

  const markAllRead = () => setItems((prev) => prev.map((n) => ({ ...n, unread: false })));

  const recent = (limit = 6) => sorted.slice(0, limit);

  const registerActionHandler = (type, fn) => {
    if (!type || typeof fn !== "function") return;
    setHandlers((prev) => ({ ...prev, [type]: fn }));
  };

  const unregisterActionHandler = (type) => {
    if (!type) return;
    setHandlers((prev) => {
      const next = { ...prev };
      delete next[type];
      return next;
    });
  };

  const defaultHandlers = {
    route: (notification, ctx = {}) => {
      const path = notification?.action?.payload?.path;
      if (path && ctx.navigate) ctx.navigate(path);
    },
    "message-intent": (notification, ctx = {}) => {
      const payload = notification?.action?.payload || {};
      const scope = payload.scope === "group" ? "group" : "direct";
      if (ctx.messages?.openConversation && payload.id) {
        ctx.messages.openConversation({ type: scope, id: payload.id });
      }
      if (ctx.messages?.openFloating) ctx.messages.openFloating();
      else if (ctx.navigate) ctx.navigate(`${"" || ""}/messages/Dubai`);
    },
    "task-intent": (notification, ctx = {}) => {
      const taskId = notification?.action?.payload?.taskId;
      if (taskId && ctx.navigate) ctx.navigate(`/tasks/view/${taskId}`);
    },
    "project-intent": (notification, ctx = {}) => {
      const { projectId, path } = notification?.action?.payload || {};
      if (path && ctx.navigate) return ctx.navigate(path);
      if (projectId && ctx.navigate)
        ctx.navigate(`${"" || ""}/projects/project-detail/${projectId}/Dubai`);
    },
  };

  const dispatchAction = (notification, ctx = {}) => {
    if (!notification?.action?.type) return;
    const handler = handlers[notification.action.type] || defaultHandlers[notification.action.type];
    if (handler) handler(notification, ctx);
    else if (import.meta.env.MODE !== "production") {
       
      // console.warn(`No handler for notification action '${notification.action.type}'`);
    }
  };

  const value = {
    items: sorted,
    unreadCount,
    markRead,
    markAllRead,
    recent,
    formatRelative,
    typeMeta,
    withMeta,
    dispatchAction,
    registerActionHandler,
    unregisterActionHandler,
  };

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
};

export const useNotificationsStore = () => useContext(NotificationsContext) || {};

export default NotificationsContext;
